import { Box, styled, SxProps, Theme } from '@mui/material';
import React, { memo, ReactNode, useId } from 'react';

const SwitchRoot = styled('div')<{ withIcon: boolean }>(
    ({ theme, withIcon }) => `
    display: inline-block;
    position: relative;
    width: ${withIcon ? theme.spacing(7) : theme.spacing(8)};
    height: ${withIcon ? theme.spacing(3.5) : theme.spacing(4)};
    font-size: ${theme.typography.fontTiny};
    color: ${theme.palette.common.white};
    cursor: pointer; 
`,
);

const SwitchTrack = styled('div')(
    ({ theme }) => `
    background-color: ${theme.components?.Switch?.backgroundColor};
    border-radius: ${theme.spacing(3)};
    width: 100%;
    height: 100%;
`,
);

const SwitchThumb = styled('span')<{ withIcon: boolean }>(
    ({ theme, withIcon }) => `
    position: absolute;
    display: inline-flex;
    align-items: center;
    background-color: ${theme.palette.common.selected};
    border-radius: ${theme.spacing(3)};
    transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
    height: 100%;
    padding: 0 10px;
    color: ${theme.palette.common.white};
    &.checked {
        transform: translateX(${withIcon ? theme.spacing(3) : theme.spacing(4)});
    }
`,
);

const SwitchText = styled(Box)`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
`;

const LabeledSwitch = memo(
    (props: {
        checked: boolean;
        onChange: () => void;
        disabled?: boolean;
        name?: string;
        onLabel: ReactNode;
        offLabel: ReactNode;
        sx?: SxProps<Theme>;
    }) => {
        const withIcon = typeof props.onLabel !== 'string';
        const reactId = useId();

        const onToggle = () => {
            !props.disabled && props.onChange?.();
        };

        return (
            <SwitchRoot
                sx={props.sx}
                withIcon={withIcon}
                role="switch"
                aria-checked={props.checked}
                aria-disabled={props.disabled || undefined}
                tabIndex={props.disabled ? -1 : 0}
                onClick={onToggle}
            >
                <SwitchTrack>
                    {props.checked && <SwitchText left={8}>{props.offLabel}</SwitchText>}
                    <SwitchThumb className={props.checked ? 'checked' : undefined} withIcon={withIcon}>
                        {props.checked ? props.onLabel : props.offLabel}
                    </SwitchThumb>
                    {!props.checked && <SwitchText right={8}>{props.onLabel}</SwitchText>}

                    <input
                        id={`labeled-switch-${reactId}`}
                        name={props.name}
                        type="checkbox"
                        checked={props.checked}
                        disabled={props.disabled}
                        readOnly
                        aria-hidden="true"
                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                    />
                </SwitchTrack>
            </SwitchRoot>
        );
    },
);

LabeledSwitch.displayName = 'LabeledSwitch';
export default LabeledSwitch;
